/**
 * Unified Messaging Service for VA Sprouts
 * Consolidates all messaging functionality with optimized realtime connections
 * 
 * Features:
 * - CRUD operations for conversations and messages
 * - Real-time subscriptions with connection management
 * - Relationship caching and security
 * - Typing indicators and message status
 * - Connection health monitoring
 */

import supabase from '../utils/supabase.js';
import messagingSecurity from '../utils/messagingSecurity.js';
import realtimeConnectionManager from '../utils/realtimeConnectionManager.js';
import { conversationService } from './conversationService.js';
import { 
  getConversationUrl, 
  getConversationBySlug, 
  getUrlFromConversation,
  validateConversationAccess 
} from '../utils/conversationUrlHelpers.js';
import cacheService from './cacheService.js';

class UnifiedMessagingService {
  constructor() {
    // Service state
    this.isInitialized = false;
    this.subscriptions = new Map();
    this.channels = new Map();
    
    // Caching system
    this.relationshipCache = new Map();
    this.conversationCache = new Map();
    this.profileCache = new Map(); // Sender profile cache to avoid N+1 queries per message
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.profileCacheTimeout = 10 * 60 * 1000; // 10 minutes for profiles
    this.maxCacheSize = 1000;
    
    // Cleanup interval (health monitoring is handled by realtimeConnectionManager)
    this.cleanupInterval = null;
    
    // Initialize the service
    this.initialize().catch(error => {
      console.error('❌ Failed to initialize UnifiedMessagingService:', error);
    });
  }

  /**
   * Initialize the messaging service
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      // Initialize realtime connection manager
      await realtimeConnectionManager.initialize();
      
      // Set up cache cleanup interval
      this.setupCleanupIntervals();
      
      this.isInitialized = true;
      
    } catch (error) {
      console.error('❌ UnifiedMessagingService initialization failed:', error);
      throw error;
    }
  }

  /**
   * Set up cleanup intervals for cache and connections
   */
  setupCleanupIntervals() {
    // Cache cleanup every minute
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredCache();
    }, 60 * 1000);

    // Pause cache cleanup when tab is hidden to conserve resources
    if (typeof document !== 'undefined') {
      this._visibilityHandler = () => {
        if (document.hidden) {
          this._pauseCleanup();
        } else {
          this._resumeCleanup();
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  /**
   * Pause cache cleanup interval when tab is hidden
   */
  _pauseCleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Resume cache cleanup interval when tab becomes visible
   */
  _resumeCleanup() {
    if (!this.cleanupInterval) {
      this.cleanupInterval = setInterval(() => {
        this.cleanupExpiredCache();
      }, 60 * 1000);
    }
  }

  /**
   * Attempt to reconnect when connection is lost
   */
  async attemptReconnection() {
    try {
      await realtimeConnectionManager.reconnect();
      
      // Re-establish critical subscriptions
      // const activeSubscriptions = Array.from(this.subscriptions.keys());
      
    } catch (error) {
      console.error('❌ Failed to reconnect messaging service:', error);
    }
  }

  // ==================== CRUD OPERATIONS ====================

  /**
   * Get conversations for a user with Redis caching and pagination support
   * @param {string} userId - The user ID
   * @param {Object} options - Pagination options
   * @param {number} options.limit - Maximum conversations to return (default 6)
   * @param {number} options.offset - Number of conversations to skip (default 0)
   * @param {boolean} options.skipCache - Skip cache and fetch fresh data
   * @returns {Object} { success, data, totalCount, hasMore, fromCache }
   */
  async getConversations(userId, options = {}) {
    const { limit = 6, offset = 0, skipCache = false } = options;
    
    try {
      // Validate UUID format
      if (!this.isValidUUID(userId)) {
        throw new Error('Invalid user ID format');
      }

      // Check Redis cache first (only for first page without skip)
      // Race against a 1s timeout so slow Redis doesn't block loading
      if (offset === 0 && !skipCache) {
        try {
          const cached = await Promise.race([
            cacheService.getConversations(userId, limit),
            new Promise(resolve => setTimeout(() => resolve(null), 1000))
          ]);
          if (cached && cached.data) {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Conversations loaded from Redis cache');
            }
            return { 
              success: true, 
              data: cached.data, 
              totalCount: cached.totalCount || cached.data.length,
              hasMore: cached.hasMore || false,
              fromCache: true 
            };
          }
        } catch {
          // Cache read failed, fall through to DB
        }
      }

      // Fetch from database with pagination
      if (process.env.NODE_ENV === 'development') {
        console.log('Fetching conversations for user ID:', userId, 'limit:', limit, 'offset:', offset);
      }
      const { data, error } = await supabase
        .rpc('get_user_conversations', { 
          user_id_param: userId,
          limit_count: limit,
          offset_count: offset
        });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Conversations RPC result:', { data, error });
      }
      
      if (error) throw error;

      // Extract total count from first row (all rows have same total_count)
      const totalCount = data && data.length > 0 ? parseInt(data[0].total_count, 10) : 0;
      const hasMore = (offset + limit) < totalCount;

      // Transform RPC response to match component expectations.
      // Backwards-compatible: the old RPC returned `id` and `recipient_name`; the
      // newer pagination RPC returns `conversation_id` and `participant_name` directly.
      // Use whichever field is present so both RPC versions work correctly.
      const transformedData = (data || []).map(conv => ({
        ...conv,
        conversation_id: conv.conversation_id || conv.id,
        participant_name: conv.participant_name || conv.recipient_name,
      }));

      // Cache the result in Redis (2 min TTL) - only cache first page
      // Fire-and-forget: don't block response on cache write
      if (offset === 0) {
        cacheService.cacheConversations(userId, limit, { 
          data: transformedData, 
          totalCount,
          hasMore 
        }, 120).catch(() => {});
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Conversations fetched, total:', totalCount, 'hasMore:', hasMore, 'transformed data:', transformedData.length > 0 ? transformedData[0] : 'empty');
      }
      return { 
        success: true, 
        data: transformedData, 
        totalCount,
        hasMore,
        fromCache: false 
      };
      
    } catch (error) {
      console.error('❌ Get conversations error:', error);
      return { success: false, error: error.message, data: [], totalCount: 0, hasMore: false };
    }
  }

  /**
   * Get messages for a conversation with Redis caching and formatting for merged conversations
   * @param {string} conversationId
   * @param {Object} [options]
   * @param {boolean} [options.skipCache] - Skip Redis cache and fetch directly from DB
   */
  async getMessages(conversationId, options = {}) {
    const { skipCache = false } = options;
    try {
      const stringConversationId = String(conversationId);
      
      if (!this.isValidUUID(stringConversationId)) {
        throw new Error('Invalid conversation ID format');
      }

      // Check Redis cache first - race against 1s timeout
      if (!skipCache) {
        try {
          const cached = await Promise.race([
            cacheService.getMessages(stringConversationId),
            new Promise(resolve => setTimeout(() => resolve(null), 1000))
          ]);
          if (cached) {
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Messages loaded from Redis cache');
            }
            return { success: true, data: cached, fromCache: true };
          }
        } catch {
          // Cache read failed, fall through to DB
        }
      }

      // Check if this conversation was merged
      const { data: conversationData, error: convError } = await supabase
        .from('conversations')
        .select('was_merged, merge_date, merged_from_ids')
        .eq('id', stringConversationId)
        .single();
        
      if (convError) {
        console.warn('Could not check if conversation was merged:', convError);
      }
      
      const wasMerged = conversationData?.was_merged || false;

      // Get messages with metadata for context in merged conversations
      const { data, error } = await supabase
        .from('messages')
        .select('id, sender_id, recipient_id, content, sent_at, message_type, is_read, read_at, attachments, metadata, sender:user_profiles!messages_sender_id_fkey(full_name, profile_picture)')
        .eq('conversation_id', stringConversationId)
        .order('sent_at', { ascending: true });
      
      if (error) throw error;
      
      // Batch-fetch job context for merged conversations instead of N+1 per-message queries
      let jobContextMap = {};
      if (wasMerged) {
        const jobIds = [...new Set(
          data
            .filter(msg => msg.metadata?.original_job_id)
            .map(msg => msg.metadata.original_job_id)
        )];        
        if (jobIds.length > 0) {
          try {
            const { data: jobsData } = await supabase
              .from('jobs')
              .select('id, title, employer:user_profiles!jobs_employer_id_fkey(company_name)')
              .in('id', jobIds);
            
            if (jobsData) {
              for (const job of jobsData) {
                jobContextMap[job.id] = {
                  title: job.title,
                  companyName: job.employer?.company_name
                };
              }
            }
          } catch (contextError) {
            console.error('Error fetching job context for merged messages:', contextError);
          }
        }
      }

      const formattedMessages = data.map((msg) => {
        const jobContext = wasMerged && msg.metadata?.original_job_id
          ? jobContextMap[msg.metadata.original_job_id]
          : null;
        
        return {
          id: msg.id,
          senderId: msg.sender_id,
          recipientId: msg.recipient_id,
          senderName: msg.sender?.full_name || 'Unknown User',
          senderAvatar: msg.sender?.profile_picture || null,
          content: msg.content,
          timestamp: msg.sent_at,
          type: msg.message_type || 'text',
          isRead: msg.is_read,
          readAt: msg.read_at || null,
          attachments: msg.attachments,
          metadata: msg.metadata,
          original_job_title: jobContext?.title || null,
          original_company_name: jobContext?.companyName || null
        };
      });

      // Cache the formatted messages in Redis (2 min TTL)
      // Fire-and-forget: don't block response on cache write
      cacheService.cacheMessages(stringConversationId, formattedMessages, 120).catch(() => {});

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Messages cached in Redis');
      }
      
      return { success: true, data: formattedMessages, fromCache: false };
      
    } catch (error) {
      console.error('❌ Get messages error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Send a new message
   */
  async sendMessage(conversationId, senderId, recipientId, content, messageType = 'text') {
    try {
      // Validate UUID format with detailed error information
      if (!this.isValidUUID(conversationId)) {
        console.error('Invalid conversation ID format:', conversationId);
        throw new Error(`Invalid conversation ID format: ${conversationId}`);
      }
      if (!this.isValidUUID(senderId)) {
        console.error('Invalid sender ID format:', senderId);
        throw new Error(`Invalid sender ID format: ${senderId}`);
      }
      if (!this.isValidUUID(recipientId)) {
        console.error('Invalid recipient ID format:', recipientId);
        throw new Error(`Invalid recipient ID format: ${recipientId}`);
      }

      // Security check
      const hasPermission = await messagingSecurity.canSendMessage(senderId, recipientId);
      if (!hasPermission.allowed) {
        throw new Error(hasPermission.reason || 'Permission denied');
      }

      const messageData = {
        conversation_id: conversationId,
        sender_id: senderId,
        recipient_id: recipientId,
        content: content.trim(),
        message_type: messageType,
        sent_at: new Date().toISOString()
      };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[UnifiedMessagingService] Inserting message');
      }
      
      const { data, error } = await supabase
        .from('messages')
        .insert(messageData)
        .select('*, sender:user_profiles!messages_sender_id_fkey(full_name, profile_picture)')
        .single();

      if (process.env.NODE_ENV === 'development') {
        console.log('[UnifiedMessagingService] Message inserted:', !!data);
      }
      
      if (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Database insert error:', error);
        }
        throw error;
      }

      // Invalidate Redis caches for both participants and increment unread count
      // Fire-and-forget: don't block message send on cache operations
      Promise.all([
        cacheService.invalidateConversations(senderId),
        cacheService.invalidateConversations(recipientId),
        cacheService.invalidateMessages(conversationId),
        cacheService.incrementUnreadCount(recipientId)
      ]).catch(() => {});

      // Also clear in-memory cache if it exists
      this.clearConversationCache(senderId);
      this.clearConversationCache(recipientId);

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Caches invalidated and unread count incremented');
      }

      return { success: true, data: data };
      
    } catch (error) {
      console.error('❌ Send message error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Mark messages as read with optimistic updates
   */
  async markMessagesAsRead(conversationId, userId) {
    try {
      if (!this.isValidUUID(conversationId) || !this.isValidUUID(userId)) {
        throw new Error('Invalid ID format');
      }

      const stringConversationId = String(conversationId);
      
      // Update messages - set both is_read and read_at for seen status
      const { error } = await supabase
        .from('messages')
        .update({ 
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('conversation_id', stringConversationId)
        .eq('recipient_id', userId)
        .eq('is_read', false);
      
      if (error) throw error;

      // Note: Conversation status update not needed since we only have basic columns
      // The is_read status on individual messages is sufficient

      // Reset unread count and invalidate Redis caches
      // Fire-and-forget: don't block response on cache invalidation
      Promise.all([
        cacheService.resetUnreadCount(userId),
        cacheService.invalidateConversations(userId),
        cacheService.invalidateMessages(stringConversationId)
      ]).catch(() => {});

      // Also clear in-memory cache if it exists
      this.clearConversationCache(userId);

      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Messages marked as read, caches invalidated, unread count reset');
      }
      
      return { success: true, error: null };
      
    } catch (error) {
      console.error('❌ Mark messages as read error:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get unread message count for a user
   */
  async getUnreadCount(userId) {
    try {
      // Check Redis cache first
      const cached = await cacheService.getUnreadCount(userId);
      if (cached !== null) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📦 Unread count from cache:', cached);
        }
        return { success: true, count: cached, fromCache: true };
      }

      // Fallback to database query
      const { data, error } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('is_read', false);

      if (error) throw error;

      const count = data?.length || 0;

      // Cache the result
      await cacheService.setUnreadCount(userId, count, 60);

      if (process.env.NODE_ENV === 'development') {
        console.log('💾 Unread count from DB (cached):', count);
      }

      return { success: true, count, fromCache: false };
    } catch (error) {
      console.error('❌ Get unread count error:', error);
      return { success: false, count: 0, error: error.message };
    }
  }

  /**
   * Create or get an existing conversation between two users with improved merge handling
   */
  async createOrGetConversation(userId, recipientId, jobId = null) {
    try {
      if (!this.isValidUUID(userId) || !this.isValidUUID(recipientId)) {
        throw new Error('Invalid user ID format');
      }

      // Security check
      const hasPermission = await messagingSecurity.canSendMessage(userId, recipientId);
      if (!hasPermission.allowed) {
        throw new Error(hasPermission.reason || 'Permission denied to create conversation');
      }

      // Use the improved database function that handles merging
      const { data: functionResult, error: functionError } = await supabase.rpc(
        'get_or_create_merged_conversation',
        {
          user1_id: userId,
          user2_id: recipientId,
          p_job_id: jobId
        }
      );
      
      if (functionError) {
        console.error('Error calling get_or_create_merged_conversation:', functionError);
        
        // Fall back to the old method if the function call fails
        const conversation = await conversationService.createOrGetConversation(
          userId, 
          recipientId, 
          jobId
        );
        
        if (conversation) {
          // Clear conversation cache for both users (Redis + in-memory)
          await Promise.all([
            cacheService.invalidateConversations(userId),
            cacheService.invalidateConversations(recipientId)
          ]);
          this.clearConversationCache(userId);
          this.clearConversationCache(recipientId);
          
          const conversationData = {
            id: conversation.id,
            participant_1: conversation.participant_1,
            participant_2: conversation.participant_2,
            job_id: conversation.job_id,
            created_at: conversation.created_at,
            merged: false
          };

          return { success: true, data: conversationData, error: null };
        }
        
        throw new Error('Failed to create or get conversation');
      }
      
      // Process the result from the function
      if (functionResult && functionResult.length > 0) {
        const result = functionResult[0];
        const conversationId = result.conversation_id;
        const wasMerged = result.merged;
        
        // Get full conversation details
        const { data: conversation, error: convError } = await supabase
          .from('conversations')
          .select('id, participant_1, participant_2, job_id, last_message_at, created_at')
          .eq('id', conversationId)
          .single();
          
        if (convError) {
          throw new Error(`Failed to get conversation details: ${convError.message}`);
        }
        
        // Clear conversation cache for both users (Redis + in-memory)
        await Promise.all([
          cacheService.invalidateConversations(userId),
          cacheService.invalidateConversations(recipientId)
        ]);
        this.clearConversationCache(userId);
        this.clearConversationCache(recipientId);
        
        const conversationData = {
          id: conversation.id,
          participant_1: conversation.participant_1,
          participant_2: conversation.participant_2,
          job_id: conversation.job_id,
          created_at: conversation.created_at,
          merged: wasMerged,
          was_merged: conversation.was_merged || wasMerged,
          merge_date: conversation.merge_date,
          merged_from_ids: conversation.merged_from_ids
        };

        return { success: true, data: conversationData, error: null };
      }

      throw new Error('Failed to create or get conversation - empty result');
      
    } catch (error) {
      console.error('❌ Create or get conversation error:', error);
      return { success: false, data: null, error: error.message };
    }
  }

  /**
   * Subscribe to conversation updates for a user
   */
  subscribeToConversations(userId, callback) {
    try {
      if (!this.isValidUUID(userId)) {
        throw new Error('Invalid user ID format');
      }

      const channelName = `user-conversations:${userId}`;
      
      // Check if already subscribed
      if (this.subscriptions.has(channelName)) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`⚠️ Already subscribed to conversations for user ${userId}`);
        }
        return this.subscriptions.get(channelName);
      }

      // Create managed channel
      const channel = realtimeConnectionManager.createChannel(channelName);
      
      // Set up listeners
      channel
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations',
          filter: `participant_1=eq.${userId}`
        }, this.handleConversationChange.bind(this, callback))
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'conversations',
          filter: `participant_2=eq.${userId}`
        }, this.handleConversationChange.bind(this, callback))
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `participant_1=eq.${userId}`
        }, this.handleConversationChange.bind(this, callback))
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `participant_2=eq.${userId}`
        }, this.handleConversationChange.bind(this, callback));

      // Subscribe with error handling
      const subscription = realtimeConnectionManager.subscribe(channelName, (status, error) => {
        if (error) {
          console.error(`❌ Conversation subscription error for ${userId}:`, error);
        } else {
          // Log subscription status in development only
          if (process.env.NODE_ENV === 'development') {
            console.log(`📡 Conversation subscription status for ${userId}: ${status}`);
          }
        }
      });

      // Store subscription
      this.subscriptions.set(channelName, { channel, subscription, userId, callback });
      this.channels.set(channelName, channel);
      
      // Log successful subscription in development only
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Subscribed to conversations for user: ${userId}`);
      }
      return subscription;
      
    } catch (error) {
      console.error('❌ Failed to subscribe to conversations:', error);
      return null;
    }
  }

  /**
   * Subscribe to messages in a conversation
   */
  subscribeToMessages(conversationId, callback) {
    try {
      if (!this.isValidUUID(conversationId)) {
        throw new Error('Invalid conversation ID format');
      }

      const channelName = `messages:${conversationId}`;
      
      // Check if already subscribed
      if (this.subscriptions.has(channelName)) {
        console.warn(`⚠️ Already subscribed to messages for conversation ${conversationId}`);
        return this.subscriptions.get(channelName);
      }

      // Create managed channel
      const channel = realtimeConnectionManager.createChannel(channelName);
      
      // Set up listeners
      channel
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        }, this.handleNewMessage.bind(this, callback))
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        }, this.handleMessageUpdate.bind(this, callback));

      // Subscribe with error handling
      const subscription = realtimeConnectionManager.subscribe(channelName, (status, error) => {
        if (error) {
          console.error(`❌ Message subscription error for ${conversationId}:`, error);
        } else {
          // Log subscription status in development only
          if (process.env.NODE_ENV === 'development') {
            console.log(`📡 Message subscription status for ${conversationId}: ${status}`);
          }
        }
      });

      // Store subscription
      this.subscriptions.set(channelName, { channel, subscription, conversationId, callback });
      this.channels.set(channelName, channel);
      
      // Log successful subscription in development only
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ Subscribed to messages for conversation: ${conversationId}`);
      }
      return subscription;
      
    } catch (error) {
      console.error('❌ Failed to subscribe to messages:', error);
      return null;
    }
  }

  /**
   * Subscribe to typing indicators
   */
  subscribeToTypingStatus(conversationId, callback) {
    try {
      if (!this.isValidUUID(conversationId)) {
        throw new Error('Invalid conversation ID format');
      }

      const channelName = `typing:${conversationId}`;
      
      // Check if already subscribed
      if (this.subscriptions.has(channelName)) {
        return this.subscriptions.get(channelName);
      }

      // Create managed channel
      const channel = realtimeConnectionManager.createChannel(channelName);
      
      // Set up broadcast listener
      channel.on('broadcast', { event: 'typing' }, (payload) => {
        if (callback) {
          callback(payload);
        }
      });

      // Subscribe with error handling
      const subscription = realtimeConnectionManager.subscribe(channelName, (status, error) => {
        if (error) {
          console.error(`❌ Typing subscription error for ${conversationId}:`, error);
        }
      });

      // Store subscription
      this.subscriptions.set(channelName, { channel, subscription, conversationId, callback });
      this.channels.set(channelName, channel);
      
      return { channel, subscription };
      
    } catch (error) {
      console.error('❌ Failed to subscribe to typing status:', error);
      return null;
    }
  }

  /**
   * Send typing indicator
   */
  sendTypingIndicator(conversationId, userId, isTyping = true) {
    try {
      const channelName = `typing:${conversationId}`;
      const channel = this.channels.get(channelName);
      
      if (!channel) {
        console.warn(`⚠️ No typing channel found for conversation ${conversationId}`);
        return;
      }

      channel.send({
        type: 'broadcast',
        event: 'typing',
        payload: {
          userId,
          conversationId,
          isTyping,
          timestamp: Date.now()
        }
      });
      
    } catch (error) {
      console.error('❌ Failed to send typing indicator:', error);
    }
  }

  // ==================== EVENT HANDLERS ====================

  /**
   * Handle conversation changes
   */
  async handleConversationChange(callback, payload) {
    try {
      // Clear relevant caches (Redis + in-memory)
      const conversation = payload.new || payload.old;
      if (conversation) {
        await Promise.all([
          cacheService.invalidateConversations(conversation.participant_1),
          cacheService.invalidateConversations(conversation.participant_2)
        ]);
        this.clearConversationCache(conversation.participant_1);
        this.clearConversationCache(conversation.participant_2);
      }

      if (callback) {
        callback(payload);
      }
      
    } catch (error) {
      console.error('❌ Error handling conversation change:', error);
    }
  }

  /**
   * Handle new messages — uses a profile cache to avoid N+1 queries per incoming message.
   * Cache hit: O(1), no DB round-trip. Miss: fetches profile once then caches for 10 min.
   */
  async handleNewMessage(callback, payload) {
    try {
      const message = payload.new;
      
      // Resolve sender profile from cache or fetch once
      let senderProfile = this._getCachedProfile(message.sender_id);
      if (!senderProfile) {
        const { data: fetchedProfile } = await supabase
          .from('user_profiles')
          .select('full_name, profile_picture')
          .eq('id', message.sender_id)
          .single();
        senderProfile = fetchedProfile || null;
        this._setCachedProfile(message.sender_id, senderProfile);
      }

      // Fetch the full message row (needed for all fields including attachments)
      const { data: fullMessage, error } = await supabase
        .from('messages')
        .select('id, sender_id, recipient_id, content, sent_at, message_type, is_read, attachments')
        .eq('id', message.id)
        .single();

      if (error || !fullMessage) {
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ Error fetching full message row:', error);
        }
        return;
      }

      if (callback) {
        const formattedMessage = {
          id: fullMessage.id,
          senderId: fullMessage.sender_id,
          recipientId: fullMessage.recipient_id,
          senderName: senderProfile?.full_name || 'Unknown User',
          senderAvatar: senderProfile?.profile_picture || null,
          content: fullMessage.content,
          timestamp: fullMessage.sent_at,
          type: fullMessage.message_type || 'text',
          isRead: fullMessage.is_read,
          attachments: fullMessage.attachments
        };
        callback(formattedMessage, 'new_message');
      }
      
    } catch (error) {
      console.error('❌ Error handling new message:', error);
    }
  }

  /**
   * Get a sender profile from the in-memory cache.
   */
  _getCachedProfile(userId) {
    const entry = this.profileCache.get(userId);
    if (!entry) return null;
    if (Date.now() - entry.timestamp > this.profileCacheTimeout) {
      this.profileCache.delete(userId);
      return null;
    }
    return entry.profile;
  }

  /**
   * Store a sender profile in the in-memory cache.
   */
  _setCachedProfile(userId, profile) {
    if (!userId) return;
    // Evict oldest entry if cache is full
    if (this.profileCache.size >= this.maxCacheSize) {
      const oldest = this.profileCache.keys().next().value;
      this.profileCache.delete(oldest);
    }
    this.profileCache.set(userId, { profile, timestamp: Date.now() });
  }

  /**
   * Handle message updates (read status, etc.)
   */
  async handleMessageUpdate(callback, payload) {
    try {
      const message = payload.new;
      
      if (callback) {
        callback(message, 'message_update');
      }
      
    } catch (error) {
      console.error('❌ Error handling message update:', error);
    }
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Unsubscribe from a specific subscription
   */
  unsubscribe(channelName) {
    try {
      const subscription = this.subscriptions.get(channelName);
      if (subscription) {
        realtimeConnectionManager.removeChannel(channelName);
        this.subscriptions.delete(channelName);
        this.channels.delete(channelName);
        // Log unsubscription in development only
        if (process.env.NODE_ENV === 'development') {
          console.log(`🗑️ Unsubscribed from: ${channelName}`);
        }
      }
    } catch (error) {
      console.error(`❌ Failed to unsubscribe from ${channelName}:`, error);
    }
  }

  /**
   * Unsubscribe from all subscriptions
   */
  unsubscribeAll() {
    try {
      console.log(`🧹 Cleaning up ${this.subscriptions.size} subscriptions...`);
      
      for (const channelName of this.subscriptions.keys()) {
        this.unsubscribe(channelName);
      }
      
      console.log('✅ All messaging subscriptions cleaned up');
    } catch (error) {
      console.error('❌ Failed to clean up all subscriptions:', error);
    }
  }

  /**
   * Validate UUID format
   */
  isValidUUID(uuid) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuid && uuidRegex.test(String(uuid));
  }

  /**
   * Clear conversation cache for user
   */
  clearConversationCache(userId) {
    const cacheKey = `conversations_${userId}`;
    this.conversationCache.delete(cacheKey);
  }

  /**
   * Clean up expired cache entries (relationship, conversation, and profile caches)
   */
  cleanupExpiredCache() {
    const now = Date.now();
    let cleaned = 0;
    
    // Clean relationship cache
    for (const [key, value] of this.relationshipCache) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.relationshipCache.delete(key);
        cleaned++;
      }
    }
    
    // Clean conversation cache
    for (const [key, value] of this.conversationCache) {
      if (now - value.timestamp > this.cacheTimeout) {
        this.conversationCache.delete(key);
        cleaned++;
      }
    }

    // Clean profile cache
    for (const [key, value] of this.profileCache) {
      if (now - value.timestamp > this.profileCacheTimeout) {
        this.profileCache.delete(key);
        cleaned++;
      }
    }
    
    // Limit cache sizes to maxCacheSize (evict oldest)
    for (const cache of [this.relationshipCache, this.conversationCache, this.profileCache]) {
      if (cache.size > this.maxCacheSize) {
        const entries = Array.from(cache.entries());
        entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
        const toDelete = entries.slice(0, entries.length - this.maxCacheSize);
        toDelete.forEach(([key]) => cache.delete(key));
        cleaned += toDelete.length;
      }
    }
    
    if (cleaned > 0 && process.env.NODE_ENV === 'development') {
      console.log(`🧹 Cleaned up ${cleaned} expired cache entries`);
    }
  }

  /**
   * Get service status and statistics
   */
  getStatus() {
    const realtimeStatus = realtimeConnectionManager.getStatus();
    
    return {
      isInitialized: this.isInitialized,
      subscriptions: {
        count: this.subscriptions.size,
        channels: Array.from(this.subscriptions.keys())
      },
      cache: {
        relationships: this.relationshipCache.size,
        conversations: this.conversationCache.size,
        profiles: this.profileCache.size,
        total: this.relationshipCache.size + this.conversationCache.size + this.profileCache.size
      },
      realtime: realtimeStatus,
      health: this.isInitialized && realtimeStatus.status === 'connected' ? 'healthy' : 'degraded'
    };
  }

  // ==================== SECURE URL METHODS ====================

  /**
   * Get secure conversation URL for a participant
   * @param {Object} otherUser - The other participant's user info
   * @param {string} jobId - Optional job ID for job-related conversations
   * @returns {string} Secure conversation URL
   */
  getSecureConversationUrl(otherUser, jobId = null) {
    try {
      return getConversationUrl(otherUser, jobId);
    } catch (error) {
      console.error('❌ Error generating secure conversation URL:', error);
      return '/messages'; // Fallback to messages list
    }
  }

  /**
   * Generate secure URL from existing conversation data
   * @param {Object} conversation - Conversation object
   * @param {string} currentUserId - Current user ID
   * @param {string} jobId - Optional job ID
   * @returns {string} Secure conversation URL
   */
  getUrlFromConversationData(conversation, currentUserId, jobId = null) {
    try {
      return getUrlFromConversation(conversation, currentUserId, jobId);
    } catch (error) {
      console.error('❌ Error generating URL from conversation data:', error);
      return '/messages';
    }
  }

  /**
   * Resolve secure conversation URL to conversation ID
   * @param {string} userId - Current user ID
   * @param {string} participantSlug - Other participant's slug from URL
   * @param {string} jobId - Optional job ID
   * @returns {Object} Resolution result with conversation ID and participant info
   */
  async resolveSecureUrl(userId, participantSlug, jobId = null) {
    try {
      if (!userId || !participantSlug) {
        throw new Error('Missing required parameters for URL resolution');
      }

      const result = await getConversationBySlug(userId, participantSlug, jobId);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to resolve conversation URL');
      }

      // Validate access
      const accessCheck = await validateConversationAccess(result.conversationId, userId);
      if (!accessCheck.hasAccess) {
        throw new Error('Access denied to conversation');
      }

      return {
        success: true,
        conversationId: result.conversationId,
        participant: result.participant
      };
      
    } catch (error) {
      console.error('❌ Error resolving secure URL:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate user access to a conversation
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - User ID to check access for
   * @returns {Object} Access validation result
   */
  async validateAccess(conversationId, userId) {
    try {
      return await validateConversationAccess(conversationId, userId);
    } catch (error) {
      console.error('❌ Error validating conversation access:', error);
      return { hasAccess: false, error: error.message };
    }
  }

  /**
   * Clean up service resources
   */
  cleanup() {
    console.log('🧹 Cleaning up UnifiedMessagingService...');

    // Remove page visibility listener
    if (typeof document !== 'undefined' && this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    
    // Clear cleanup interval
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    
    // Unsubscribe from all
    this.unsubscribeAll();
    
    // Clear caches
    this.relationshipCache.clear();
    this.conversationCache.clear();
    this.profileCache.clear();
    
    this.isInitialized = false;
    console.log('✅ UnifiedMessagingService cleanup completed');
  }
}

// Create and export singleton instance
const unifiedMessagingService = new UnifiedMessagingService();

export default unifiedMessagingService;
